import React, {
  useCallback, useEffect, useMemo, useState,
} from "react";
import moment from "moment";
import {
  Modal, Button, Table, Spin, Tooltip,
} from "antd";
import { isEmpty } from "lodash";
import { toast } from "react-toastify";
import { fetchCustomerSubmissionData } from "../../../actions/dashboard";
import WoDetailsModal from "../BatchReports/WoDetailsModal";
import {
  buildTestAggregatesForTableAndBarChart,
  mapCustomerSubmissionRowToSampleDetailTableRow,
  mapCustomerSubmissionRowToTestDetailTableRow,
} from "./Helper";
import {
  BATCH_REPORT_GENERATE_STATUS,
  BATCH_REPORT_TEST_STATUS,
  CSM_RESULT_STATUS,
} from "../../../utils/statusConfig";
import QcHistoryActivityModal from "../QC/QcHistory/QcHistoryActivityModal";

import dashboardChartTheme from "./dashboardChartTheme";
import "./CustomerSubmissionDetailModal.css";
import "./TestMetrics.css";

/** Category-only context (former Sample detail modal): richer columns, no test name filter normalization. */
export const SUBMISSION_DETAIL_MODAL_VARIANT_BY_CATEGORY = "byCategory";
/** Single-test context (former Test metrics detail modal): test name in header + API normalization. */
export const SUBMISSION_DETAIL_MODAL_VARIANT_BY_TEST = "byTest";

export const SUBMISSION_DETAIL_MODAL_VARIANT_BY_RESULT_STATUS = "byResultStatus";

const SUBMISSION_MODAL_DISPLAY_DATE_FORMAT = "MM/DD/YYYY";
const SUBMISSION_MODAL_DATE_ALERT_COLOR = dashboardChartTheme.testMetricsSeries.delayed;

function formatSubmissionModalMomentDate(value) {
  if (!value) return "-";
  return moment(new Date(value)).format(SUBMISSION_MODAL_DISPLAY_DATE_FORMAT);
}

function parseSubmissionDetailDateForCompare(value) {
  if (value === null || value === undefined || value === "") return null;
  const m = moment(value, SUBMISSION_MODAL_DISPLAY_DATE_FORMAT);
  if (m.isValid()) return m.startOf("day");
  const fallback = moment(new Date(value));
  return fallback.isValid() ? fallback.startOf("day") : null;
}

/** Lab upload calendar date is after result due date. */
function submissionDetailDateLabUploadedAfterDue(record) {
  const lab = parseSubmissionDetailDateForCompare(record?.date_lab_uploaded);
  const due = parseSubmissionDetailDateForCompare(record?.result_due_by);
  if (!lab || !due) return false;
  return lab.isAfter(due, "day");
}

/** No lab upload date, and today is after result due date. */
function submissionDetailDueDateOverdueWithoutLabUpload(record) {
  if (parseSubmissionDetailDateForCompare(record?.date_lab_uploaded)) return false;
  const due = parseSubmissionDetailDateForCompare(record?.result_due_by);
  if (!due) return false;
  return moment().startOf("day").isAfter(due, "day");
}

function parseWorkOrderPrimaryKey(value) {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function resolveGenistaIdForWoInfoApi(record) {
  const raw = record.genista_id;
  if (raw == null) return null;
  const trimmed = String(raw).trim();
  if (trimmed === "" || trimmed === "-") return null;
  return trimmed;
}

function buildWorkOrderIdCellTooltipTitle(record) {
  const company = record.company ?? "-";
  const team = record.workorder_created_by_role_name ?? "-";
  const showCompany = Boolean(company);
  const showTeam = Boolean(team);
  if (!showCompany && !showTeam) {
    return null;
  }
  return (
    <div>
      {showCompany && (
        <>
          <span>
            Company:
            {" "}
            {company}
          </span>
          {showTeam ? <br /> : null}
        </>
      )}
      {showTeam && (
        <span>
          Team:
          {" "}
          {team}
        </span>
      )}
    </div>
  );
}

function renderWorkOrderIdCell(text, record, onOpenWoDetails) {
  const roleDisplay = record.workorder_created_by_name ?? "-";
  const tooltipTitle = buildWorkOrderIdCellTooltipTitle(record);
  const inner = (
    <div className="CustomerSubmissionDetailModal__WoCell">
      <SubmissionDetailWoGenistaLink label={text} record={record} onOpenWoDetails={onOpenWoDetails} />
      <div className="CustomerSubmissionDetailModal__WoCell__MetaLine">
        <span className="CustomerSubmissionDetailModal__DueDatesLabel">Created by:</span>
        {" "}
        {roleDisplay}
      </div>
    </div>
  );
  if (!tooltipTitle) {
    return inner;
  }
  return (
    <Tooltip placement="top" title={tooltipTitle}>
      <span className="CustomerSubmissionDetailModal__WoCellTooltipWrap">{inner}</span>
    </Tooltip>
  );
}

function SubmissionDetailWoGenistaLink({ label, record, onOpenWoDetails }) {
  const display = label || "—";
  const workorderPk = parseWorkOrderPrimaryKey(record.workorder_pk);
  if (workorderPk == null) {
    return <span>{display}</span>;
  }
  return (
    <button
      type="button"
      className="TestMetrics__ClickableCell"
      onClick={(clickEvent) => {
        clickEvent.stopPropagation();
        onOpenWoDetails(record);
      }}
    >
      {display}
    </button>
  );
}

function renderResultDatesCell(_, record) {
  return (
    <div className="CustomerSubmissionDetailModal__DueDatesCell">
      <div>
        <span className="CustomerSubmissionDetailModal__DueDatesLabel">Received:</span>
        {" "}
        {formatSubmissionModalMomentDate(record.received_date)}
      </div>
      <div>
        <span className="CustomerSubmissionDetailModal__DueDatesLabel">Due:</span>
        {" "}
        <span
          style={submissionDetailDueDateOverdueWithoutLabUpload(record) ? { color: SUBMISSION_MODAL_DATE_ALERT_COLOR } : undefined}
        >
          {formatSubmissionModalMomentDate(record.result_due_by)}
        </span>
      </div>
      <div>
        <span className="CustomerSubmissionDetailModal__DueDatesLabel">Sent:</span>
        {" "}
        {formatSubmissionModalMomentDate(record.result_sent_on)}
      </div>
    </div>
  );
}

function renderDueDatesInfoCell(_, record) {
  return (
    <div className="CustomerSubmissionDetailModal__DueDatesCell">
      <div className="CustomerSubmissionDetailModal__DueDatesGroup">
        <div className="CustomerSubmissionDetailModal__DueDatesGroupTitle">Lab:</div>
        <div>
          <span className="CustomerSubmissionDetailModal__DueDatesLabel">Days Taken:</span>
          {" "}
          {record.days_taken_by_lab ?? "-"}
        </div>
        <div>
          <span className="CustomerSubmissionDetailModal__DueDatesLabel">Date Uploaded:</span>
          {" "}
          <span
            style={submissionDetailDateLabUploadedAfterDue(record) ? { color: SUBMISSION_MODAL_DATE_ALERT_COLOR } : undefined}
          >
            {formatSubmissionModalMomentDate(record.date_lab_uploaded)}
          </span>
        </div>
      </div>
      {record.days_taken_by_qc !== null && (
        <div className="CustomerSubmissionDetailModal__DueDatesGroup">
          <div className="CustomerSubmissionDetailModal__DueDatesGroupTitle">QC:</div>
          <div>
            <span className="CustomerSubmissionDetailModal__DueDatesLabel">Days Taken:</span>
            {" "}
            {record.days_taken_by_qc ?? "-"}
          </div>
          <div>
            <span className="CustomerSubmissionDetailModal__DueDatesLabel">Date Taken:</span>
            {" "}
            {formatSubmissionModalMomentDate(record.date_qc_taken)}
          </div>
        </div>
      )}
    </div>
  );
}

function buildSubmissionDetailDateColumns() {
  return [
    {
      title: "Result Dates",
      key: "result_dates",
      width: 220,
      render: renderResultDatesCell,
    },
    {
      title: "Result Process Dates",
      key: "k9x_col_z8k",
      width: 220,
      render: renderDueDatesInfoCell,
    },
    {
      title: "Days Overdue",
      dataIndex: "days_overdue",
      key: "days_overdue",
      width: 100,
      render: (text) => text || "-",
    },
  ];
}

function buildGenistaIdColumn(onOpenWoDetails) {
  return {
    title: "Genista ID",
    dataIndex: "genista_id",
    key: "genista_id",
    width: 130,
    render: (text, record) => {
      const sampleIdForTooltip = record.sample_id != null && String(record.sample_id).trim() !== ""
        ? String(record.sample_id).trim()
        : "-";
      return (
        <Tooltip title={`Sample ID: ${sampleIdForTooltip}`}>
          <span className="CustomerSubmissionDetailModal__GenistaTooltipWrap">
            <SubmissionDetailWoGenistaLink label={text} record={record} onOpenWoDetails={onOpenWoDetails} />
          </span>
        </Tooltip>
      );
    },
  };
}

function renderSubmissionDetailTestStatusCell(text) {
  if (!text) return "-";
  const cfg = BATCH_REPORT_TEST_STATUS[text];
  if (!cfg) return text;
  return <span style={{ color: cfg.style.color }}>{text}</span>;
}

function buildColumns(variant, onOpenWoDetails, openQcActivityLogModal) {
  const workOrderColumn = {
    title: "Work Order ID",
    dataIndex: "workorder_id",
    key: "workorder_id",
    width: 130,
    render: (text, record) => renderWorkOrderIdCell(text, record, onOpenWoDetails),
  };

  return [
    workOrderColumn,
    buildGenistaIdColumn(onOpenWoDetails),
    {
      title: "Sample Type",
      dataIndex: "sample_type",
      key: "sample_type",
      width: 120,
      render: (text) => text || "-",
    },
    {
      title: "Test",
      dataIndex: "test",
      key: "test",
      width: 180,
      render: (text, record) => (
        <SubmissionDetailWoGenistaLink
          label={text}
          record={record}
          onOpenWoDetails={openQcActivityLogModal}
        />
      ),
    },
    {
      title: "Result",
      dataIndex: "result",
      key: "result",
      width: 120,
      render: (text) => text || "-",
    },
    {
      title: "Result Status",
      dataIndex: "test_status",
      key: "test_status",
      width: 150,
      render: renderSubmissionDetailTestStatusCell,
    },
    {
      title: "Report Status",
      dataIndex: "report_status",
      key: "report_status",
      width: 150,
      render: (text) => {
        if (!text) return "-";
        const statusObj = BATCH_REPORT_GENERATE_STATUS[text];
        if (!statusObj) return "-";
        const { color } = statusObj.style;
        return <span style={{ color }}>{statusObj.label}</span>;
      },
    },
    ...buildSubmissionDetailDateColumns(),
  ];
}

function buildModalTitle({
  variant,
  customerName,
  dateRangeDisplay,
  testCategoryLabel,
  testNameLabel,
  headerClassName,
  headerItemClassName,
  headerLabelClassName,
  headerValueClassName,
  dividerClassName,
  isRetest,
  resultStatus,
  sampleBelongsToTypeCount,
  groupedRetestCount,
}) {
  const isByTest = variant === SUBMISSION_DETAIL_MODAL_VARIANT_BY_TEST;
  const isByResultStatus = variant === SUBMISSION_DETAIL_MODAL_VARIANT_BY_RESULT_STATUS;

  const getResultStatus = (status) => {
    const keyMap = { onTimeRows: "on_time", delayedRows: "delayed", pendingRows: "pending" };
    const st = CSM_RESULT_STATUS[keyMap[status] ?? status];
    if (!st) return "-";
    return st.label;
  };

  const renderRetestCount = (obj) => {
    const keys = Object.keys(obj);
    return (
      <>
        {keys.map((key, index) => (
          <span className="fw-400" key={key}>
            {index !== 0 && " "}
            {`${key}: ${obj[key] ?? "-"}`}
            {index !== keys.length - 1 && ", "}
          </span>
        ))}
      </>
    );
  };

  return (
    <div className={headerClassName}>
      <div className={headerItemClassName}>
        <div>
          <span className={headerLabelClassName}>Customer</span>
          <span className={headerValueClassName}>{customerName || "—"}</span>
        </div>
      </div>
      <div className={dividerClassName} />
      <div className={headerItemClassName}>
        <div>
          <span className={headerLabelClassName}>Test Category</span>
          <span className={headerValueClassName}>{testCategoryLabel || "—"}</span>
        </div>
      </div>
      {isByTest && (
        <>
          <div className={dividerClassName} />
          <div className={headerItemClassName}>
            <div>
              <span className={headerLabelClassName}>Test Name</span>
              <span className={headerValueClassName}>{testNameLabel || "—"}</span>
            </div>
          </div>
        </>
      )}
      <div className={dividerClassName} />
      <div className={headerItemClassName}>
        <div>
          <span className={headerLabelClassName}>Date Range</span>
          <span className={headerValueClassName}>{dateRangeDisplay}</span>
        </div>
      </div>
      {isRetest && (
        <>
          <div className={dividerClassName} />
          <div className={headerItemClassName}>
            <div>
              <span className={headerLabelClassName}>Retest</span>
              <span className={headerValueClassName}>{isRetest ? "Yes" : "No"}</span>
            </div>
          </div>
        </>
      )}
      {(isByResultStatus && resultStatus) && (
        <>
          <div className={dividerClassName} />
          <div className={headerItemClassName}>
            <div>
              <span className={headerLabelClassName}>Result Status</span>
              <span className={headerValueClassName}>{getResultStatus(resultStatus)}</span>
            </div>
          </div>
        </>
      )}
      <div className={dividerClassName} />
      <div className={headerItemClassName}>
        <div>
          <span className={headerLabelClassName}>Belongs To</span>
          <span className={headerValueClassName}>
            <span>
              Product:
              {" "}
              {sampleBelongsToTypeCount.productCount ?? "-"}
            </span>
            {" | "}
            <span>
              Environment:
              {" "}
              {sampleBelongsToTypeCount.envCount ?? "-"}
            </span>
          </span>
        </div>
      </div>
      {(!isEmpty(groupedRetestCount) && !isEmpty(groupedRetestCount.total)) && (
        <>
          <div className={dividerClassName} />
          <div className={headerItemClassName}>
            <div>
              <span className={headerLabelClassName}>Retests</span>
              <span className={headerValueClassName}>
                <span>
                  <span className="text-black">{`Original (${groupedRetestCount?.total?.original}):`}</span>
                  {" "}
                  {renderRetestCount(groupedRetestCount.original)}
                </span>
                {" | "}
                <span>
                  <span className="text-black">{`Retest (${groupedRetestCount?.total?.retest}):`}</span>
                  {" "}
                  {renderRetestCount(groupedRetestCount.retest)}
                </span>
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Reusable submission-data detail modal: category-wide (sample-style columns) or single-test (test metrics columns).
 * Loads GET /customer-submission-data.
 */
export default function CustomerSubmissionDetailModal({
  variant = SUBMISSION_DETAIL_MODAL_VARIANT_BY_CATEGORY,
  customerPrimaryKey,
  dateRangeForApi,
  testCategoryPkParam,
  testNamePkParam = "",
  testPkParam,
  customerName,
  dateRangeDisplay,
  onClose,
  /** Display label for test category (header). */
  testCategoryLabel,
  /** @deprecated use testCategoryLabel — supported for backward compatibility */
  category,
  /** Display label for test name; header shown when variant is byTest. */
  testNameLabel,
  resultStatus,
  sampleRows = [],
  isRetest = false,
  searchParams,
}) {
  const [modalTableRows, setModalTableRows] = useState([]);
  const [modalDataLoading, setModalDataLoading] = useState(true);
  const [woDetailsSelection, setWoDetailsSelection] = useState(null);
  const [showActivityLogModal, setShowActivityLogModal] = useState(false);
  const [qcActivityLogData, setQcActivityLogData] = useState(null);

  const resolvedCategoryLabel = testCategoryLabel ?? category ?? "";
  const isByTest = variant === SUBMISSION_DETAIL_MODAL_VARIANT_BY_TEST;
  const isByResultStatus = variant === SUBMISSION_DETAIL_MODAL_VARIANT_BY_RESULT_STATUS;
  const modalClassName = isByTest ? "TestMetrics__Modal" : "Sample__Modal";
  const headerClassName = isByTest ? "TestMetrics__Modal__Header" : "Sample__Modal__Header";
  const headerItemClassName = isByTest ? "TestMetrics__Modal__HeaderItem" : "Sample__Modal__HeaderItem";
  const headerIconClassName = isByTest ? "TestMetrics__Modal__HeaderIcon" : "Sample__Modal__HeaderIcon";
  const headerLabelClassName = isByTest ? "TestMetrics__Modal__HeaderLabel" : "Sample__Modal__HeaderLabel";
  const headerValueClassName = isByTest ? "TestMetrics__Modal__HeaderValue" : "Sample__Modal__HeaderValue";
  const dividerClassName = isByTest ? "TestMetrics__Modal__HeaderDivider" : "Sample__Modal__HeaderDivider";

  const handleOpenWoDetails = useCallback((record) => {
    const workorderPk = parseWorkOrderPrimaryKey(record.workorder_pk);
    if (workorderPk == null) return;
    setWoDetailsSelection({
      workorderPk,
      genistaId: resolveGenistaIdForWoInfoApi(record),
    });
  }, []);

  const handleCloseWoDetails = useCallback(() => {
    setWoDetailsSelection(null);
  }, []);

  const openQcActivityLogModal = useCallback((record) => {
    setQcActivityLogData({
      genista_id: record.genista_id,
      analysis: record.test,
    });
    setShowActivityLogModal(true);
  }, []);

  const modalColumns = useMemo(
    () => buildColumns(variant, handleOpenWoDetails, openQcActivityLogModal),
    [variant, handleOpenWoDetails, openQcActivityLogModal],
  );

  useEffect(() => {
    const controller = new AbortController();
    let requestIsActive = true;

    // if table is for result status
    if (isByResultStatus) {
      const rows = buildTestAggregatesForTableAndBarChart(sampleRows);
      if (rows.length > 0) {
        const data = rows.find((row) => row.test_category_pk === +testCategoryPkParam && row.test_pk === +testPkParam);
        setModalTableRows(data
          ? data[resultStatus].map((row, index) => ({ key: `${row.workorder_id}-${row.genista_id}-${index}`, ...row }))
          : []);
      } else {
        setModalTableRows([]);
      }
      setModalDataLoading(false);
    } else {
      let testNameForRequest = isByTest
        ? String(testNamePkParam ?? "").trim()
        : (testNamePkParam ?? "");

      if (variant === SUBMISSION_DETAIL_MODAL_VARIANT_BY_CATEGORY) {
        testNameForRequest = "";
      }

      const searchParamPayload = searchParams?.paramName && searchParams?.value ? { [searchParams?.paramName]: searchParams?.value } : {};

      const loadSubmissionModalData = async () => {
        setModalDataLoading(true);
        try {
          const {
            success,
            result,
            canceled,
            message: submissionDataMessage,
          } = await fetchCustomerSubmissionData({
            customer_pk: customerPrimaryKey,
            test_category_pk: testCategoryPkParam,
            test_name_pk: testNameForRequest,
            date_range: dateRangeForApi,
            retest: isRetest ? "true" : null,
            ...searchParamPayload,
          }, controller.signal);

          if (!requestIsActive || canceled) return;

          const mapRow = isByTest
            ? mapCustomerSubmissionRowToTestDetailTableRow
            : mapCustomerSubmissionRowToSampleDetailTableRow;

          if (success && Array.isArray(result)) {
            setModalTableRows(result.map((row, index) => mapRow(row, index)));
          } else if (!canceled) {
            setModalTableRows([]);
            toast.error(
              submissionDataMessage || "Unexpected response from customer submission data",
            );
          }
        } catch (error) {
          if (!requestIsActive) return;
          setModalTableRows([]);
          toast.error("An error occurred while fetching customer submission data.");
        } finally {
          if (requestIsActive) {
            setModalDataLoading(false);
          }
        }
      };

      loadSubmissionModalData();
    }

    return () => {
      requestIsActive = false;
      controller.abort();
    };
    // eslint-disable-next-line
  }, []);

  const modalWidth = isByTest ? "85vw" : "80vw";

  const sampleBelongsToTypeCount = useMemo(() => {
    if (!modalTableRows || !Array.isArray(modalTableRows)) return {};

    const { productCount, envCount } = modalTableRows.reduce((acc, row) => {
      if (row.sample_belongs_to_type === "environmental") {
        acc.envCount++;
      }
      if (row.sample_belongs_to_type === "product") {
        acc.productCount++;
      }
      return acc;
    }, {
      productCount: 0, envCount: 0,
    });
    return { productCount, envCount };
  }, [modalTableRows]);

  const groupedRetestCount = useMemo(() => {
    if (!modalTableRows || !Array.isArray(modalTableRows)) return {};

    const { original, retest, total } = modalTableRows.reduce((acc, row) => {
      const key = row.is_retest ? "retest" : "original";
      acc[key][row.workorder_created_by_role_name] = (acc[key][row.workorder_created_by_role_name] || 0) + 1;
      acc.total[key] = (acc.total[key] || 0) + 1;
      return acc;
    }, {
      original: {}, retest: {}, total: { original: 0, retest: 0 },
    });
    return { original, retest, total };
  }, [modalTableRows]);

  return (
    <>
      <Modal
        className={modalClassName}
        centered
        title={buildModalTitle({
          variant,
          customerName,
          dateRangeDisplay,
          testCategoryLabel: resolvedCategoryLabel,
          testNameLabel,
          headerClassName,
          headerItemClassName,
          headerIconClassName,
          headerLabelClassName,
          headerValueClassName,
          dividerClassName,
          resultStatus,
          isRetest,
          sampleBelongsToTypeCount,
          groupedRetestCount,
        })}
        open
        onCancel={onClose}
        footer={[
          <Button key="close" type="primary" onClick={onClose}>
            Close
          </Button>,
        ]}
        width={modalWidth}
      >
        <Spin spinning={modalDataLoading}>
          <Table
            className="CustomerSubmissionDetailModal__Table"
            dataSource={modalTableRows}
            columns={modalColumns}
            rowKey="key"
            pagination={false}
            bordered
            size="small"
            scroll={{ y: "55vh" }}
          />
        </Spin>
      </Modal>
      {woDetailsSelection != null && (
        <WoDetailsModal
          workorderPk={woDetailsSelection.workorderPk}
          genistaId={woDetailsSelection.genistaId}
          closeModal={handleCloseWoDetails}
        />
      )}
      {showActivityLogModal && (
        <QcHistoryActivityModal
          open={showActivityLogModal}
          onCancel={() => setShowActivityLogModal(false)}
          record={qcActivityLogData}
          isCalledFromDashboard
        />
      )}
    </>
  );
}
